import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import moment from "moment";
import { Col, Empty, Row } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  Wrapper,
} from './styles';
import {
  DesktopContentWrapper,
  ListingHeaderContentWrapper, MobileContentWrapper,
} from '../SearchListings/styles';
import AvailableBuses from '../../components/AvailableBuses';
import HeaderNavigation from "../../components/HeaderNavigation/Road";

import { GET_SUPPORTED_ROAD_ROUTES, SEARCH_BUS_ROUTES } from '../../utils/constants';
import { getAPI, postAPI } from '../../utils/api';
import { buildSearchPayload } from './helpers';
import { useRoadSearchStore } from '../../store/roadSearch/roadSearchStore';
import BusDateFilter from "../../components/AvailableBuses/BusDateFilter";
import RoadSelectInfo from "../../components/AvailableBuses/RoadSelectInfo";
import Sort from "../../components/ListingFilter/FlightFilters/Sort";
import RoadListingFilterMobile from "../../components/ListingFilter/RoadListingFilterMobile";
import RoadListingFilterDesktop from "../../components/ListingFilter/RoadListingFilterDesktop";
import BusRequestLoading from "../../components/RequestLoading/BusRequestLoading";
import EmptyResult from "../../components/EmptyResult";
import { isMobileScreen } from "../../utils/helpers";
import { BusResultList as mockBusData } from "../../data";
import { useStore } from '../../store/global/appStore';
import { getTerminalName } from '../../components/TripSummary/helper';

function SearchRoadListings() {
  const drawerContainerRef = useRef(null);
  const navigate = useNavigate();
  const isMobile = isMobileScreen();
  const [busResults, setBusResults] = useState<RoadSearchResult>();
  const [filteredBusResults, setFilteredBusResults] = useState<RoadSearchResult>();
  const [sortBy, setSortBy] = useState("cheapest");
  const [selectedBuses, setSelectedBuses] = useState<AvailableBus[]>([]);
  const [selectedOperators, setSelectedOperators] = useState<string[]>([]);
  const [operators, setOperators] = useState<SelectOption[]>([]);
  const [error, setError] = useState<string | null>();
  const [openModify, setOpenModify] = useState<boolean>(false);
  const [selectedPriceRange, setSelectedPriceRange] = useState<[number, number] | null>(null);
  const [openAllFilters, setOpenAllFilters] = useState<boolean>(false);

  const [loadingData, setLoadingData] = useState<boolean>(true);
  const { formData, selectedResult, updateFormData, updateStoreData } = useRoadSearchStore();
  const { roadRoutes, setData } = useStore();
  const isOneWay = formData.TripType === 'one_way';
  const isReturning = selectedBuses.length > 0;
  const filteredResultKey = isReturning ? "SecondLeg" : "FirstLeg";

  const departureName = getTerminalName(roadRoutes, formData.DepartureId);
  const arrivalName = getTerminalName(roadRoutes, formData.ArrivalId);

  const selectInfoFrom = isReturning ? arrivalName : departureName;
  const selectInfoTo = isReturning ? departureName : arrivalName;

  const legResult = useMemo(() => {
    if (!busResults) {
      return {
        TotalCount: 0,
        ResultList: [],
      }
    }

    return isReturning ? busResults?.SecondLeg : busResults?.FirstLeg;
  }, [busResults, isReturning]);

  const filteredLegResult: RoadLegResult = useMemo(() => {
    if (!filteredBusResults) {
      return {
        TotalCount: 0,
        ResultList: [],
      }
    }

    return isReturning ? filteredBusResults?.SecondLeg : filteredBusResults?.FirstLeg;
  }, [filteredBusResults, isReturning]);

  const searchBuses = useCallback(async (payload = formData) => {
    setLoadingData(true);
    setError(null);
    setSelectedBuses([]);
    updateFormData({
      tripsSessionId: null,
      numberOfAdults: 1,
      selectedBus: null,
      selectedResult: null,
      selectedBusSeat: null,
    });

    const reformedPayload = buildSearchPayload(payload);
    const searchResults = await postAPI(SEARCH_BUS_ROUTES, reformedPayload);

     
    if (searchResults.status === 200) {
      setBusResults(searchResults.data);
      setFilteredBusResults(searchResults.data);
      setLoadingData(false);
    } else {
      setError(searchResults.data);
      setLoadingData(false);
    }
  }, [formData]);

  useEffect(() => {
    searchBuses();
  }, []);

  useEffect(() => {
  if (roadRoutes.length > 0) return;

  const fetchRoadRoutes = async () => {
    const roadRoutesAPI = await getAPI(GET_SUPPORTED_ROAD_ROUTES);

    if (roadRoutesAPI.status === 200) {
      setData(roadRoutesAPI.data.Data, 'roadRoutes');
      localStorage.setItem('roadRoutes', JSON.stringify(roadRoutesAPI.data.Data));
    }
  };

  fetchRoadRoutes();
}, [roadRoutes.length, setData]);

  const handleNavigation = () => {
    if (selectedResult) {
      updateStoreData({ selectedResult: null });
    } else {
      navigate(-1);
    }
  };

  const handleResetFilters = () => {
    setSelectedOperators(operators.map(a => a.value));
    setSortBy("cheapest");
    setSelectedPriceRange(null);
    setFilteredBusResults(busResults);
  }

  const handleBookingRedirect = () => {
    if (isMobile) {
      navigate('/itinerary/road');
    } else {
      navigate('/passenger-details/road');
    }
  };

  const handleOperatorFiltering = (operatorValues: string[]) => {
    if (operatorValues.length === 0 || operatorValues.length === operators.length) {
      setFilteredBusResults(busResults);
      setSelectedOperators(operatorValues);
      handleSort(sortBy, busResults[filteredResultKey]);
    } else {
      let filteredTotalCount = 0;
      const filteredResults = busResults[filteredResultKey].ResultList.map((result) => {
        const filteredBuses = result.AvailableBuses.filter((bus) => {
          const opName = bus.Operator?.Name || result.AgentName;
          return operatorValues.includes(opName);
        });
        filteredTotalCount += filteredBuses.length;
        return {
          ...result,
          AvailableBuses: filteredBuses
        };
      }).filter((result) => result.AvailableBuses.length > 0);

      const filteredLegResult = {
        TotalCount: filteredTotalCount,
        ResultList: filteredResults
      }

      setFilteredBusResults({
        ...busResults,
        [filteredResultKey]: {
          ...filteredLegResult
        }
      });
      setSelectedOperators(operatorValues);
      handleSort(sortBy, filteredLegResult);
    }
  };

  const handleApplyAllRoadFilters = (filters: {
    operators: string[];
    price: [number, number] | null;
    sortBy: string;
  }) => {
    if (!busResults) return;

    const baseLeg = busResults[filteredResultKey];
    if (!baseLeg) return;

    let filteredTotalCount = 0;
    let filteredResults = baseLeg.ResultList.map((result) => {
      const opFiltered = result.AvailableBuses.filter((bus) => {
        const opName = bus.Operator?.Name || result.AgentName;
        return filters.operators.includes(opName);
      });
      return {
        ...result,
        AvailableBuses: opFiltered
      };
    }).filter((result) => result.AvailableBuses.length > 0);

    if (filters.price) {
      const [min, max] = filters.price;
      filteredResults = filteredResults.map((result) => {
        const priceFiltered = result.AvailableBuses.filter((bus) => {
          return bus.TotalFare >= min && bus.TotalFare <= max;
        });
        return {
          ...result,
          AvailableBuses: priceFiltered
        };
      }).filter((result) => result.AvailableBuses.length > 0);
    }

    filteredTotalCount = filteredResults.reduce((sum, res) => sum + res.AvailableBuses.length, 0);

    const filteredLegResult = {
      TotalCount: filteredTotalCount,
      ResultList: filteredResults
    };

    setSelectedOperators(filters.operators);
    setSelectedPriceRange(filters.price);
    setSortBy(filters.sortBy);
    handleSort(filters.sortBy, filteredLegResult);
  };


  const handleFilterByPriceRange = (range: [number, number]) => {
    setSelectedPriceRange(range);
    const [min, max] = range;

    const legAvailableBuses = legResult.ResultList.map((result) => {
      return result.AvailableBuses;
    });

    let filteredTotalCount = 0;

    const filteredAvailableBuses = legAvailableBuses.map((buses) => {
      const filteredBuses = buses.filter((bus) => {
        return bus.TotalFare >= min && bus.TotalFare <= max;
      });

      filteredTotalCount += filteredBuses.length;
      return filteredBuses;
    });

    const filteredLegResult = {
      TotalCount: filteredTotalCount,
      ResultList: legResult.ResultList.map((result, index) => {
        return {
          ...result,
          AvailableBuses: filteredAvailableBuses[index]
        }
      })
    };

    setFilteredBusResults({
      ...busResults,
      [filteredResultKey]: {
        ...filteredLegResult
      }
    });
  };

  const handleSort = (value: string, data = filteredLegResult) => {
    setSortBy(value);
    switch (value) {
      case "cheapest":
        handleSortByPrice("asc", data)
        return;
      case "fastest":
        handleSortByDuration("asc", data)
        return;
      case "earliest":
        handleSortByDate("desc", data)
        return;
    }
  }

  useEffect(() => {
    let uniqueOperators: string[] = [];
    legResult.ResultList.forEach((d) => {
      d.AvailableBuses.forEach((bus) => {
        const opName = bus.Operator?.Name || d.AgentName;
        if (opName && !uniqueOperators.includes(opName)) {
          uniqueOperators.push(opName);
        }
      });
    });

    setOperators(uniqueOperators.map((a) => ({ label: a, value: a })));
    setSelectedOperators(uniqueOperators);
    setSelectedPriceRange(null);
  }, [legResult]);

  const getDurationInMinutes = (bus: any): number => {
    if (bus.Duration) {
      const match = bus.Duration.match(/(\d+)h:(\d+)m/);
      if (match) {
        return parseInt(match[1]) * 60 + parseInt(match[2]);
      }
      const matchHour = bus.Duration.match(/(\d+)h/);
      if (matchHour) {
        return parseInt(matchHour[1]) * 60;
      }
    }

    if (bus.EstimatedArrivalDate && bus.EstimatedDepartureDate) {
      const a = moment(bus.EstimatedArrivalDate);
      const d = moment(bus.EstimatedDepartureDate);
      if (a.isValid() && d.isValid()) {
        return a.diff(d, 'minutes');
      }
    }
    return 999999;
  };

  const handleSortByPrice = (order: string | null, data = filteredLegResult) => {
    const sortedResult = data.ResultList.map((result) => {
      return {
        ...result,
        AvailableBuses: [...result.AvailableBuses].sort((a, b) => {
          if (order === 'asc') {
            return a.TotalFare - b.TotalFare;
          }
          return b.TotalFare - a.TotalFare;
        })
      };
    });

    sortedResult.sort((a, b) => {
      const aTopBus = a.AvailableBuses[0];
      const bTopBus = b.AvailableBuses[0];
      if (!aTopBus) return 1;
      if (!bTopBus) return -1;
      return order === 'asc' ? aTopBus.TotalFare - bTopBus.TotalFare : bTopBus.TotalFare - aTopBus.TotalFare;
    });

    setFilteredBusResults({
      ...filteredBusResults,
      [filteredResultKey]: {
        TotalCount: data.TotalCount,
        ResultList: sortedResult
      }
    });
  };

  const handleSortByDuration = (order: string | null, data = filteredLegResult) => {
    const sortedResult = data.ResultList.map((result) => {
      return {
        ...result,
        AvailableBuses: [...result.AvailableBuses].sort((a, b) => {
          const aDuration = getDurationInMinutes(a);
          const bDuration = getDurationInMinutes(b);

          if (order === 'asc') {
            return aDuration - bDuration;
          }
          return bDuration - aDuration;
        })
      };
    });

    sortedResult.sort((a, b) => {
      const aTopBus = a.AvailableBuses[0];
      const bTopBus = b.AvailableBuses[0];
      if (!aTopBus) return 1;
      if (!bTopBus) return -1;
      const aDuration = getDurationInMinutes(aTopBus);
      const bDuration = getDurationInMinutes(bTopBus);
      return order === 'asc' ? aDuration - bDuration : bDuration - aDuration;
    });

    setFilteredBusResults({
      ...filteredBusResults,
      [filteredResultKey]: {
        TotalCount: data.TotalCount,
        ResultList: sortedResult
      }
    });
  };

  const handleSortByDate = (order: string | null, data = filteredLegResult) => {
    const sortedResult = data.ResultList.map((result) => {
      return {
        ...result,
        AvailableBuses: [...result.AvailableBuses].sort((a, b) => {
          const aTime = moment(a.EstimatedDepartureDate);
          const bTime = moment(b.EstimatedDepartureDate);
          if (order === 'asc') {
            return bTime.diff(aTime);
          }
          return aTime.diff(bTime);
        })
      };
    });

    sortedResult.sort((a, b) => {
      const aTopBus = a.AvailableBuses[0];
      const bTopBus = b.AvailableBuses[0];
      if (!aTopBus) return 1;
      if (!bTopBus) return -1;
      const aTime = moment(aTopBus.EstimatedDepartureDate);
      const bTime = moment(bTopBus.EstimatedDepartureDate);
      if (order === 'asc') {
        return bTime.diff(aTime);
      }
      return aTime.diff(bTime);
    });

    setFilteredBusResults({
      ...filteredBusResults,
      [filteredResultKey]: {
        TotalCount: data.TotalCount,
        ResultList: sortedResult
      }
    });
  };

  const totalCount = legResult.TotalCount;
  const resultCount = filteredLegResult.TotalCount;
  const hasResults = totalCount > 0;

  const cheapestFares = useMemo(() => {
    if (!busResults || totalCount === 0) {
      return null;
    }

    const cheapestDeparture = busResults.FirstLeg.ResultList[0]?.AvailableBuses[0]?.TotalFare;
    const cheapestReturn = busResults.SecondLeg.ResultList[0]?.AvailableBuses[0]?.TotalFare;

    return {
      cheapestDeparture,
      cheapestReturn
    };
  }, [busResults, totalCount]);

  const handleEmptyResult = () => {
    if (hasResults) {
      handleResetFilters();
    } else {
      setOpenModify(true)
    }
  };

  const farePriceRange = useMemo(() => {
    if (!busResults || totalCount === 0) {
      return { min: 0, max: 0 };
    }

    const results = isReturning ? busResults.SecondLeg.ResultList : busResults.FirstLeg.ResultList;

    const fares = results.map(f => f.AvailableBuses.map(b => b.TotalFare)).flat();

    return {
      min: Math.min(...fares), max: Math.max(...fares)
    };
  }, [busResults, totalCount, isReturning]);

  const renderAvailableBusResults = () => {
    return (
      <AvailableBuses
        results={filteredBusResults}
        selectedBuses={selectedBuses}
        onBusSelect={(b) => {
          handleResetFilters();
          setSelectedBuses(b)
        }}
        onBookingRedirect={handleBookingRedirect}
        cheapestFares={cheapestFares}
      />
    )
  }

  if (loadingData) {
    return <BusRequestLoading />
  }

  return (
    <Wrapper>
      <Row>
        <Col span={24}>
          <HeaderNavigation
            navigateHandler={handleNavigation}
            modify={openModify}
            onModifyChange={(value) => setOpenModify(value)}
            refetchSearch={searchBuses}
            onOpenFilters={() => setOpenAllFilters(true)}
          />
        </Col>
      </Row>

      {/*  Desktop devices */}
      <Row>
        <Col xs={0} lg={24}>
          <DesktopContentWrapper className="container" ref={drawerContainerRef}>
            <Row gutter={8}>
              <Col span={5}>
                <RoadListingFilterDesktop
                  handleOperatorFiltering={handleOperatorFiltering}
                  handleFilterByPriceRange={handleFilterByPriceRange}
                  farePriceRange={farePriceRange}
                  selectedOperators={selectedOperators}
                  operators={operators}
                  onResetFilters={handleResetFilters}
                  selectedPriceRange={selectedPriceRange}
                />
              </Col>

              <Col span={18} offset={1}>
                {
                  isOneWay && <BusDateFilter refetchSearch={searchBuses} />
                }
                <ListingHeaderContentWrapper>
                  <RoadSelectInfo
                    count={resultCount}
                    isReturning={isReturning}
                    from={selectInfoFrom}
                    to={selectInfoTo}
                  />
                  <Sort onChange={handleSort} sortBy={sortBy} />
                </ListingHeaderContentWrapper>

                {
                  !error && resultCount === 0 && <EmptyResult onAction={handleEmptyResult} emptyOnFilter={hasResults} />
                }

                {(error) ? <Empty description={error} /> : renderAvailableBusResults()}
              </Col>
            </Row>
          </DesktopContentWrapper>
        </Col>
      </Row>

      {/*  Mobile Devices */}
      <Row>
        <Col xs={24} lg={0}>
          {
            isOneWay && <BusDateFilter refetchSearch={searchBuses} />
          }
          <MobileContentWrapper>
            <Col xs={24} lg={0}>
              <RoadSelectInfo
                count={resultCount}
                isReturning={isReturning}
                from={selectInfoFrom}
                to={selectInfoTo}
              />
              <RoadListingFilterMobile
                handleOperatorFiltering={handleOperatorFiltering}
                handleSort={handleSort}
                handleFilterByPriceRange={handleFilterByPriceRange}
                farePriceRange={farePriceRange}
                selectedOperators={selectedOperators}
                operators={operators}
                sortBy={sortBy}
                openAllFilters={openAllFilters}
                setOpenAllFilters={setOpenAllFilters}
                onApplyAllFilters={handleApplyAllRoadFilters}
              />

              {
                !error && resultCount === 0 && <EmptyResult onAction={handleEmptyResult} emptyOnFilter={hasResults} />
              }

              {(error) ? <Empty description={error} /> : renderAvailableBusResults()}
            </Col>
          </MobileContentWrapper>
        </Col>
      </Row>
    </Wrapper>
  )
}

export default SearchRoadListings;
